import {ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Post, PostComment, PostCommentLikeResponse, PostCommentTree} from '../../_types/content';
import {ContentService} from '../../_services/content.service';
import {filter, mergeMap, Observable, take, tap} from 'rxjs';
import {SharedStateService} from '../../_services/shared-state.service';
import {AuthService} from '../../_services/auth.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '../confirmation-dialog/confirmation-dialog.component';
import {ConfirmationDialogData, GenericResponse, ReplyData, ReplyEvent} from '../../_types/shared';
import {SnackbarService} from '../../_services/snackbar.service';
import {animate, keyframes, state, style, transition, trigger} from '@angular/animations';
import {ActivatedRoute} from '@angular/router';
import {CommentFlare, Community, UserAccessLevel} from "../../_types/communities";
import {ThemeService} from "../../_services/theme.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {RewardsService} from "../../_services/rewards.service";
import {CommunitiesService} from "../../_services/communities.service";
import {SegmentRule} from "../../_types/segments";

@Component({
    selector: 'app-comment',
    templateUrl: './comment.component.html',
    styleUrl: './comment.component.scss',
    animations: [
        trigger('doubleBlink', [
            state('blink', style({})),
            transition('void => blink', [
                animate('300ms', style({backgroundColor: 'var(--theme-accent-300)'})),
                animate('300ms', style({})),
                animate('300ms', style({backgroundColor: 'var(--theme-accent-300)'})),
                animate('300ms', style({}))
            ]),
        ]),
        trigger('likeAnimation', [
            state('inactive', style({
                transform: 'scale(1)'
            })),
            state('active', style({
                transform: 'scale(1)'
            })),
            transition('inactive => active', [
                animate('300ms ease-in-out', keyframes([
                    style({transform: 'scale(1)', offset: 0}),
                    style({transform: 'scale(1.4)', offset: 0.5}),
                    style({transform: 'scale(1)', offset: 1.0})
                ]))
            ]),
            transition('active => inactive', [
                animate('100ms ease-out')
            ])
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CommentComponent implements OnInit {
    @Input({required: true}) comment!: PostCommentTree;
    @Input({required: true}) post!: Post;
    @Input() child: boolean = false;
    @Output() activeCommentOutput: EventEmitter<any> = new EventEmitter<any>();
    @Output() minimiseReplies: EventEmitter<boolean> = new EventEmitter<boolean>();
    editing: boolean = false;
    userInfoMenuOpen: boolean = false;
    blinkState: string = 'void';
    community: Community | null = null
    isMobile: boolean = false;
    streaks: any[] = [];
    commentFlares: CommentFlare[] = [];

    protected readonly transition = transition;
    protected readonly unescape = unescape;

    constructor(
        private readonly contentService: ContentService,
        private readonly sharedStateService: SharedStateService,
        private readonly authService: AuthService,
        private readonly cdr: ChangeDetectorRef,
        private readonly rewardsService: RewardsService,
        private readonly dialog: MatDialog,
        private readonly snackbarService: SnackbarService,
        private readonly communitiesService: CommunitiesService,
        private readonly route: ActivatedRoute,
        protected themeService: ThemeService
    ) {
        this.communitiesService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
            });

    }

    get replyCommentId(): Observable<number | null> {
        return this.sharedStateService.getVariable('replyCommentId');
    }

    get userCanDeleteComment(): boolean {
        return this.authService.isSuperUser() || this.hasCommunityRole(['full_admin']);
    }

    get isSuperuser(): boolean {
        return this.authService.isSuperUser();
    }

    get userIsNotCommentAuthor(): boolean {
        return this.authService.getUser()?.id !== this.comment.author?.id;
    }

    get isRequiredScrollToComment(): boolean {
        return !!this.route.snapshot.queryParamMap.get('comment') && +this.route.snapshot.queryParamMap.get('comment')! === this.comment.id;
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any): void {
        this.isMobile = window.innerWidth <= 550;
    }

    ngOnInit(): void {
        this.isMobile = window.innerWidth <= 550;
        // author id is -1 if anonymous user
        if (this.community?.id && this.comment.author?.id && this.comment.author?.id > 0) {
            this.rewardsService.getUserStreaks(this.community?.id, this.comment.author?.id).subscribe(r => {
                this.streaks = r;
                this.cdr.detectChanges();
            }, error => {
                console.error(error);
            })
        }
        if (this.community?.id && (this.isSuperuser || this.hasCommunityRole(['full_admin']))) {
            this.communitiesService.getCommentFlares(this.community.id).subscribe(r => {
                this.commentFlares = r;
            }, error => {
                console.error(error);
            })
        }
    }

    selectFlare(flareId: string): void {
        // if same flare is selected then toggle it off (unselect)
        let removeFlare = this.comment.flare?.id == flareId
        this.contentService.patchComment(this.post.id, this.comment.id, {"flare_id": removeFlare ? null : flareId}).subscribe(r => {
            this.comment.flare = r.data.flare;
            this.cdr.detectChanges();
            this.snackbarService.openSuccessSnackBar('Comment Flare ' + (removeFlare ? 'removed' : 'added'));
        }, e => {
            this.snackbarService.openErrorSnackBar(e);
        })
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.communitiesService.userHasCommunityRole(roles);
    }

    likeComment(comment: PostComment): void {
        const obs$: Observable<GenericResponse<PostCommentLikeResponse>> = comment.is_liked ? this.contentService.unlikeComment(this.post.id, comment.id) : this.contentService.likeComment(this.post.id, comment.id);
        this.comment.is_liked = !this.comment.is_liked;
        if (this.comment.is_disliked) {
            this.comment.is_disliked = false;
        }
        obs$.pipe(take(1)).subscribe(res => {
            this.comment.likes_count = res.data.likes_count;
            this.comment.dislikes_count = res.data.dislikes_count;
            this.cdr.detectChanges();
        });
    }

    dislikeComment(comment: PostComment): void {
        const obs$: Observable<GenericResponse<PostCommentLikeResponse>> = comment.is_disliked ? this.contentService.unDislikeComment(this.post.id, comment.id) : this.contentService.dislikeComment(this.post.id, comment.id);
        this.comment.is_disliked = !this.comment.is_disliked;
        if (this.comment.is_liked) {
            this.comment.is_liked = false;
        }
        obs$.pipe(take(1)).subscribe(res => {
            this.comment.likes_count = res.data.likes_count;
            this.comment.dislikes_count = res.data.dislikes_count;
            this.cdr.detectChanges();
        });
    }

    replyComment(comment: PostComment): void {
        this.sharedStateService.setVariable('replyCommentId', comment.id);
    }

    deleteComment(): void {
        const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
            panelClass: 'mediumWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to delete this comment? <br>(<i>Please note</i>: all replies comments will also be deleted)',
                title: 'Delete comment',
                showHeader: true
            }
        });
        dialogRef.afterClosed()
            .pipe(
                take(1),
                filter(result => result === true),
                mergeMap((): Observable<void> => this.contentService.deletePostComments(this.post.id, this.comment.id)),
                mergeMap((): Observable<PostComment[]> => this.contentService.getPostComments(this.post.id)),
                tap(res => {
                    this.post.comments_count = res.length;
                    this.sharedStateService.updateModalComments(res);
                })
            )
            .subscribe((): void => {
                this.snackbarService.openSuccessSnackBar('Deleted successfully');
            });
    }

    reportComment(): void {
        const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
            panelClass: 'mediumWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to report this comment?',
                title: 'Report comment',
                showHeader: true
            }
        });
        dialogRef.afterClosed()
            .pipe(
                take(1),
                filter(result => result === true),
                mergeMap((): Observable<void> => this.contentService.reportPostComments(this.post.id, this.comment.id))
            )
            .subscribe((): void => {
                this.comment.is_flagged_for_review = true;
                this.snackbarService.openSuccessSnackBar('Reported successful');
            });
    }

    moderateComment(): void {
        this.contentService.patchComment(this.post.id, this.comment.id, {"moderated": true}).subscribe(r => {
            this.comment.moderated = true;
            this.cdr.detectChanges();
            this.snackbarService.openSuccessSnackBar('Comment marked as moderated');
        }, e => {
            this.snackbarService.openErrorSnackBar(e);
        })
    }

    unflagComment(): void {
        this.contentService.patchComment(this.post.id, this.comment.id, {"is_flagged_for_review": false}).subscribe(r => {
            this.comment.is_flagged_for_review = false;
            this.cdr.detectChanges();
            this.snackbarService.openSuccessSnackBar('Comment un-flagged');
        }, e => {
            this.snackbarService.openErrorSnackBar(e);
        })
    }

    hideComment(): void {
        this.contentService.patchComment(this.post.id, this.comment.id, {"hidden": !this.comment.hidden}).subscribe(r => {
            let hidden: string = 'hidden'
            if (this.comment.hidden) {
                hidden = 'un-' + hidden
            }
            this.comment.hidden = !this.comment.hidden;
            this.cdr.detectChanges();
            this.snackbarService.openSuccessSnackBar('Comment ' + hidden);
        }, e => {
            this.snackbarService.openErrorSnackBar(e);
        })
    }

    pinComment(): void {
        this.contentService.patchComment(this.post.id, this.comment.id, {"is_pinned": !this.comment.is_pinned}).subscribe(r => {
            let text: string = 'pinned'
            if (this.comment.hidden) {
                text = 'un-' + text
            }
            this.comment.is_pinned = !this.comment.is_pinned;
            this.contentService.getPostComments(this.post.id).pipe(take(1))
                .subscribe(res => this.sharedStateService.updateModalComments(res));
            this.cdr.detectChanges();
            this.snackbarService.openSuccessSnackBar('Comment ' + text);
        }, e => {
            this.snackbarService.openErrorSnackBar(e);
        })
    }

    startBlinkAnimation(): void {
        this.blinkState = 'blink';
    }

    editComment(): void {
        this.editing = true;
    }

    updateComment(event: ReplyEvent<ReplyData>): void {
        this.comment = event.data?.comment! as PostCommentTree;
        this.editing = false;
        this.cdr.detectChanges();
    }

    getAccessDisplay(accessLevel: UserAccessLevel): string | null {
        if(!accessLevel || !this.community) return null;
        switch (accessLevel) {
            case 'member':
                return this.community.comment_tag_title_for_user_role_member ? this.community.comment_tag_title_for_user_role_member : null
            case 'client':
                return this.community.comment_tag_title_for_user_role_client ? this.community.comment_tag_title_for_user_role_client : null
            case 'moderator':
                return this.community.comment_tag_title_for_user_role_moderator ? this.community.comment_tag_title_for_user_role_moderator : null
            case 'full_admin':
                return this.community.comment_tag_title_for_user_role_full_admin ? this.community.comment_tag_title_for_user_role_full_admin : null
            case 'reports_only':
                return this.community.comment_tag_title_for_user_role_reports_only ? this.community.comment_tag_title_for_user_role_reports_only : null
            case 'observer':
                return this.community.comment_tag_title_for_user_role_observer ? this.community.comment_tag_title_for_user_role_observer : null
        }
    }
}
